import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  type ColumnVisibilityState,
  flexRender,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type React from 'react';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { EmptyState } from '../empty-state';
import { HeaderGloss } from '../popover/header-gloss';
import type { StrictColumnDef } from './column';
import { type AppRow, type AppTable, appTableFeatures } from './features';
import { SortIndicator } from './sort-indicator';
import { TABLE_PADDING, useResponsiveColumnSizing } from './use-column-sizing';

type TanStackTableProps<T extends RowData> = {
  /** Data items to display in the table */
  data: T[];
  /** Column definitions (TanStack ColumnDef format) */
  columns: StrictColumnDef<T>[];
  /** Row height in pixels (default: 28) */
  rowHeight?: number;

  // Sorting (programmatic, controlled externally)
  /** Current sorting state */
  sorting?: SortingState;
  /** Callback when sorting changes */
  onSortingChange?: OnChangeFn<SortingState>;

  // Column visibility
  /** Current column visibility state */
  columnVisibility?: ColumnVisibilityState;

  /** Column display order — array of column IDs */
  columnOrder?: string[];

  // Row selection (single row highlighting)
  /** Current row selection state */
  rowSelection?: RowSelectionState;
  /** Callback when row selection changes */
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;

  /** Custom content rendered above the column headers (e.g., summary row with aggregates) */
  headerContent?: React.ReactNode;

  /** Get row ID for selection/keys (defaults to index) */
  getRowId?: (original: T, index: number) => string;

  /** Callback when table instance is ready */
  onTableReady?: (table: AppTable<T>) => void;

  /** External ref for the scroll container (use with useScrollPreservation) */
  scrollRef?: RefObject<HTMLDivElement | null>;

  /** Notification when the scroll container scrolls */
  onScroll?: () => void;

  /** Custom row className based on row data and index */
  getRowClassName?: (row: T, index: number) => string;

  /**
   * Stripes rendered as reserved lanes on both the left and right of every
   * row (with matching spacers in the header). The reserved width is
   * STRIPE_WIDTH on every row; the returned `width` controls the visible
   * stripe within each lane (so the row width stays constant even when
   * stripe widths differ between rows). Returning undefined leaves the
   * lanes invisible.
   */
  getRowStripe?: (row: T, index: number) => { color: string; width: number } | undefined;

  /**
   * Fires when a row is clicked. Receives the underlying TanStack `Row` (use
   * `row.original` for the item, `row.id` for the table-level ID, `event` for
   * modifier keys). Selection-on-click is no longer automatic — pair this with
   * `useRouteRowSelection`'s `onRowClick` for the standard toggle behavior.
   */
  onRowClick?: (row: AppRow<T>, event: React.MouseEvent) => void;

  /**
   * Rendered when there are zero data rows. Defaults to a plain
   * "No matching items." message when omitted.
   */
  emptyState?: React.ReactNode;
};

const STRIPE_WIDTH = 6;

// Shared no-op state selector: TanStackTable controls every state slice via
// props, so nothing needs to re-render off the table store itself.
const emptyStateSelector = () => ({});

export function TanStackTable<T extends RowData>({
  data,
  columns,
  rowHeight = 28,
  sorting,
  onSortingChange,
  columnVisibility,
  columnOrder,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  onTableReady,
  headerContent,
  onScroll,
  scrollRef,
  getRowClassName,
  getRowStripe,
  onRowClick,
  emptyState,
}: TanStackTableProps<T>) {
  const stripeWidth = getRowStripe ? STRIPE_WIDTH : 0;
  const internalRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollRef ?? internalRef;

  // Compute responsive column widths from visible columns only
  const visibleColumnsForSizing = useMemo(() => {
    if (!columnVisibility) {
      return columns;
    }

    return columns.filter((col) => columnVisibility[col.id] !== false);
  }, [columns, columnVisibility]);
  const { columnSizing, totalWidth, windowWidth } =
    useResponsiveColumnSizing(visibleColumnsForSizing);

  // Create table instance. The options object must be referentially stable
  // between renders: useTable returns a new table object whenever it changes,
  // and that identity feeds the onTableReady effect below.
  const table = useTable(
    useMemo(
      () => ({
        features: appTableFeatures,
        data,
        columns,
        // rowSortingFeature's own default is `sortUndefined: 1`, which makes
        // the comparator call row.getValue() on BOTH rows of every comparison
        // just to probe for undefined. defaultColumn is merged after the
        // feature defaults, so opting out here covers every column in every
        // table — including any built without defineColumn, and any that a
        // future wrapper forgets to carry the option through.
        defaultColumn: { sortUndefined: false },
        state: {
          sorting,
          columnVisibility,
          columnOrder,
          rowSelection: rowSelection ?? {},
          columnSizing,
        },
        onSortingChange,
        onRowSelectionChange,
        enableMultiSort: true,
        enableRowSelection: true,
        enableMultiRowSelection: false, // Single selection only
        getRowId,
      }),
      [
        data,
        columns,
        sorting,
        columnVisibility,
        columnOrder,
        rowSelection,
        columnSizing,
        onSortingChange,
        onRowSelectionChange,
        getRowId,
      ]
    ),
    // Every state slice is controlled through props, so re-renders already
    // arrive via props — an empty selection opts out of store-driven renders.
    emptyStateSelector
  );

  // Notify parent when table is ready (triggers re-render for CSV/Print buttons).
  // useTable returns a fresh snapshot object whenever options change, but its
  // methods read live store state — so notify once per underlying instance
  // (keyed by the stable store reference), not once per snapshot. Re-notifying
  // every snapshot would setState in the parent on every data/sort/filter
  // change and double-render the page.
  const notifiedStore = useRef<typeof table.store | null>(null);
  useEffect(() => {
    if (onTableReady && notifiedStore.current !== table.store) {
      notifiedStore.current = table.store;
      onTableReady(table);
    }
  }, [table, onTableReady]);

  const { rows } = table.getRowModel();

  // Set up row virtualization
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    overscan: 30,
  });

  // TanStack Virtual's getMeasurements memo depends on count and itemSizeCache, not estimateSize.
  // When rowHeight changes without a count change, cached sizes remain stale. measure() clears the
  // cache (new Map reference) so the memo re-runs and picks up the new estimateSize.
  // biome-ignore lint/correctness/useExhaustiveDependencies: virtualizer is a stable reference
  useEffect(() => {
    virtualizer.measure();
  }, [rowHeight, virtualizer]);

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const contentWidth = totalWidth - TABLE_PADDING + 2 * stripeWidth;

  // Memoize header groups - must depend on columns and columnVisibility to update
  // (table is a stable reference that doesn't change when columns or visibility are updated)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run when columns/visibility change
  const headerGroups = useMemo(
    () => table.getHeaderGroups(),
    [table, columns, columnVisibility, columnOrder]
  );

  // Order numbers only earn their place when more than one column is sorted.
  const isMultiSort = (sorting?.length ?? 0) > 1;

  if (data.length === 0) {
    return (
      emptyState ?? (
        <EmptyState icon={<MagnifyingGlassIcon className="size-8" />} title="No matching items." />
      )
    );
  }

  // Dynamic grid rows: auto for each header row, 1fr for the scrollable content
  const gridTemplateRows = headerContent ? 'auto auto 1fr' : 'auto 1fr';

  return (
    <div className="w-full h-full overflow-x-auto font-mono">
      <div
        className="h-full grid px-4"
        style={{
          minWidth: Math.max(totalWidth + 2 * stripeWidth, windowWidth),
          gridTemplateRows,
        }}
      >
        {/* Custom header content (e.g. summary row with aggregates). */}
        {headerContent && (
          <div
            className="flex flex-row border-t border-gray-200 divide-x divide-gray-300"
            style={{ width: contentWidth, paddingLeft: stripeWidth, paddingRight: stripeWidth }}
          >
            {headerContent}
          </div>
        )}

        {/* Column headers — bottom border explicitly colored so it survives a
            contrast clamp; the global base-layer default of gray-200 collapses
            into the body's bg-surface-row-even white under low contrast. */}
        <div className="border-b-2 border-gray-400">
          {headerGroups.map((headerGroup) => (
            <div
              key={headerGroup.id}
              className="flex flex-row border-t border-gray-200 divide-x divide-gray-300"
              style={{ width: contentWidth, paddingLeft: stripeWidth, paddingRight: stripeWidth }}
            >
              {headerGroup.headers.map((header) => {
                const width = columnSizing[header.id] ?? header.getSize();
                const sortDirection = header.column.getIsSorted();
                const gloss = header.column.columnDef.meta?.gloss;

                return (
                  <div
                    key={header.id}
                    style={{ width, minWidth: width, maxWidth: width }}
                    className="shrink-0 flex flex-col gap-0.5 px-1 py-1.5 text-left text-sm font-semibold text-gray-900"
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        {/*
                         * Meta lane — a fixed-height strip reserved for the
                         * sort mark and info icon. Keeping them out of the
                         * label's box is the whole point: an adornment that
                         * shares the label's flow collides with it the moment
                         * the label wraps to two lines. The sort mark is
                         * pinned to the left edge and the info icon to the
                         * right edge, each in its own slot, so both keep a
                         * consistent spot whether or not the other is present.
                         */}
                        <div className="flex h-4 shrink-0 items-center justify-between">
                          <span className="flex items-center">
                            {sortDirection && (
                              <SortIndicator
                                direction={sortDirection}
                                order={isMultiSort ? header.column.getSortIndex() + 1 : undefined}
                              />
                            )}
                          </span>
                          <span className="flex items-center">
                            {gloss && <HeaderGloss gloss={gloss} />}
                          </span>
                        </div>
                        {/*
                         * Label owns the full cell width and bottom-aligns, so
                         * single- and two-line headers share a baseline.
                         */}
                        <div className="flex flex-1 items-end">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Virtualized rows */}
        <div
          ref={parentRef}
          className="overflow-auto"
          onScroll={onScroll}
          style={{
            width: contentWidth,
            minWidth: contentWidth,
            maxWidth: contentWidth,
          }}
        >
          <div
            style={{
              height: `${totalSize}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isSelected = row.getIsSelected();
              // 0-indexed: an even index is the 1st / 3rd / 5th visible row,
              // i.e. the "odd" (tinted) zebra lane in the surface-row tokens.
              const isEven = virtualRow.index % 2 === 0;
              const customRowClassName = getRowClassName?.(row.original, virtualRow.index) ?? '';
              const stripe =
                stripeWidth > 0 ? getRowStripe?.(row.original, virtualRow.index) : undefined;
              // has-[[data-state=open]] keeps the row's rail lit while a popover
              // anchored inside it is open, so moving the cursor off the row and
              // into the (portaled) card doesn't drop the "which row am I on" cue.
              const rowStateClass = isSelected
                ? 'row-rails-selected hover:row-rails-selected-hover has-[[data-state=open]]:row-rails-selected-hover'
                : 'hover:row-rails-hover has-[[data-state=open]]:row-rails-hover';

              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: Row is primarily mouse-driven
                // biome-ignore lint/a11y/noStaticElementInteractions: Virtualized table row
                <div
                  key={row.id}
                  data-index={virtualRow.index}
                  data-row-id={row.id}
                  onClick={(event) => onRowClick?.(row, event)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingLeft: stripeWidth,
                    paddingRight: stripeWidth,
                  }}
                  className={`flex flex-row divide-x divide-gray-300 ${onRowClick ? 'cursor-pointer' : 'cursor-default'} text-gray-900 ${rowStateClass} ${isEven ? 'bg-surface-row-odd' : 'bg-surface-row-even'} ${customRowClassName}`}
                >
                  {stripe && (
                    <>
                      <div
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          width: stripe.width,
                          backgroundColor: stripe.color,
                        }}
                      />
                      <div
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          right: 0,
                          width: stripe.width,
                          backgroundColor: stripe.color,
                        }}
                      />
                    </>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const width = columnSizing[cell.column.id] ?? cell.column.getSize();

                    return (
                      <div
                        key={cell.id}
                        style={{ width, minWidth: width, maxWidth: width }}
                        className="grid px-1 py-1 text-xs truncate text-clip content-center shrink-0"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
